/**
 * TPOGen module — centralized data-testid registry.
 *
 * Two authoring styles live here and they must not be mixed:
 *
 * 1. **Derived ids** — built from the tenant config's own field identity
 *    (`tpoFieldTestId` and friends). These cannot drift from the config
 *    because they are computed from the same `name` the rules engine uses.
 * 2. **Static chrome ids** — hand-written constants for toolbars, tabs,
 *    modals, and page shells, which have no config identity to derive from.
 *
 * Grammar for every id in this file: `tpo_{surface}_{element}` in
 * snake_case, matching `/^[a-z0-9]+(_[a-z0-9]+)*$/`. `scripts/validateTestIds.ts`
 * enforces this.
 *
 * Never inline a `data-testid` string literal in a component — import from
 * here so the QA manifest stays complete.
 *
 * No id is declared for a state the UI does not render. TPOGen surfaces query
 * failures as transient toasts rather than a persistent panel, so there are no
 * per-screen error ids: a suite asserts the absence of the screen's content
 * instead. The one exception is the config gate, which does render a blocking
 * error state and therefore has one.
 */

import snakeCase from 'lodash/snakeCase';

// =============================================================================
// Derivation helpers
// =============================================================================

const SLUG_CACHE = new Map<string, string>();

/**
 * Config key -> id segment. `inStoreStartDate` -> `in_store_start_date`.
 *
 * Backed by `lodash/snakeCase` rather than a hand-rolled regex because a
 * naive `([a-z0-9])([A-Z])` split mangles consecutive capitals — an
 * acronym-bearing key like `eventNSRoi` would collapse to `event_nsroi`.
 *
 * Two behaviours to know about:
 * - Digits become their own word, so `dim1` -> `dim_1`.
 * - Already-snake_case backend keys pass through unchanged, so the same
 *   helper is safe on both camelCase config names and snake_case API keys.
 *
 * Cached because this runs for every field on every form render.
 */
export const slugifyFieldName = (name: string): string => {
    const cached = SLUG_CACHE.get(name);
    if (cached !== undefined) return cached;
    const slug = snakeCase(name);
    SLUG_CACHE.set(name, slug);
    return slug;
};

/** The form surfaces that own a field identity. Mirrors the rules engine's `Scope`. */
export type TpoScope = 'plan' | 'event' | 'promo' | 'optimize' | 'metric';

/** Primary id for one config-driven field. */
export const tpoFieldTestId = (scope: TpoScope, fieldName: string): string =>
    `tpo_${scope}_${slugifyFieldName(fieldName)}`;

/** Sub-parts of one control: unit toggles, range halves, error text. */
export const tpoFieldPartTestId = (scope: TpoScope, fieldName: string, part: string): string =>
    `${tpoFieldTestId(scope, fieldName)}_${slugifyFieldName(part)}`;

/**
 * Dropdown option, keyed on the option `value` (never the label, which is
 * translated).
 *
 * Returns `undefined` when the value has no slug — a purely symbolic value such
 * as `%` or `$` reduces to the empty string. Emitting an id anyway would append
 * a bare `_option_`, and since every symbolic value reduces to the same empty
 * slug, the two halves of a unit toggle would answer to one locator. A suite
 * would then pass while asserting the wrong node, which is worse than having no
 * locator, so these are published as a gap instead. Callers spread the result or
 * pass it straight to `data-testid`, both of which drop `undefined`.
 */
export const tpoOptionTestId = (baseId: string, optionValue: string | number): string | undefined => {
    const slug = slugifyFieldName(String(optionValue));
    return slug ? `${baseId}_option_${slug}` : undefined;
};

/** Derives a part id from an already-built base id. */
export const tpoPartTestId = (baseId: string, part: string): string => `${baseId}_${slugifyFieldName(part)}`;

/** `dimSelect` fields carry no `name`; they derive from dimension + attributeType. */
export const tpoDimFieldName = (dimension: string, attributeType: string): string => `${dimension}-${attributeType}`;

/**
 * Exposes the rule engine's per-field decisions as DOM attributes.
 *
 * Rule behaviour is the hardest part of TPOGen to test and the most
 * regression-prone: whether a field is disabled or required is computed at
 * runtime from other field values, so a suite cannot infer it from the config.
 * Emitting it lets QA assert the rule outcome directly instead of inferring it
 * from styling.
 *
 * Absent rather than `"false"` when the flag is off, so `[data-state-disabled]`
 * works as a presence selector.
 */
export const tpoStateAttrs = (state?: { disabled?: boolean; required?: boolean }) => ({
    'data-state-disabled': state?.disabled ? 'true' : undefined,
    'data-state-required': state?.required ? 'true' : undefined,
});

// -----------------------------------------------------------------------------
// Promo metric grid
//
// Metric keys are slugified, not passed through. They arrive from
// `GET /tpo_gen/config/` mostly snake_case, but not reliably — the live `gp`
// catalog serves keys like `event_Inc_gross_margin_dollars`, whose capital would
// put the id outside the published grammar. `slugifyFieldName` is idempotent on
// the well-formed majority, so this only touches the outliers, and it keeps a
// single rule for turning any backend key into an id segment.
//
// `ppgId` is left verbatim: it is an opaque row identity, handled the same way
// as `rowKey` in the generic table helpers below.
// -----------------------------------------------------------------------------

export const tpoMetricColTestId = (metricKey: string): string => `tpo_promo_metric_col_${slugifyFieldName(metricKey)}`;
export const tpoMetricCellTestId = (ppgId: string | number, metricKey: string): string =>
    `tpo_promo_metric_cell_${ppgId}_${slugifyFieldName(metricKey)}`;
export const tpoMetricTotalTestId = (metricKey: string): string =>
    `tpo_promo_metric_total_${slugifyFieldName(metricKey)}`;
export const tpoMetricRowTestId = (ppgId: string | number): string => `tpo_promo_metrics_row_${ppgId}`;

// -----------------------------------------------------------------------------
// Generic table helpers
//
// `surface` is a static, hand-written segment (`calendar`, `plan_index`, ...);
// `columnKey` and `rowKey` come from config or backend keys and pass through.
// -----------------------------------------------------------------------------

export const tpoTableColTestId = (surface: string, columnKey: string): string => `tpo_${surface}_col_${columnKey}`;
export const tpoTableCellTestId = (surface: string, rowKey: string | number, columnKey: string): string =>
    `tpo_${surface}_cell_${rowKey}_${columnKey}`;
export const tpoTableRowTestId = (surface: string, rowKey: string | number): string => `tpo_${surface}_row_${rowKey}`;
export const tpoTableExpandTestId = (surface: string, rowKey: string | number): string =>
    `tpo_${surface}_expand_${rowKey}`;

// -----------------------------------------------------------------------------
// Calendar period columns
//
// Period keys are ISO dates (`2026-01-05`). The dashes are slugged to
// underscores so every emitted id matches the same `[a-z0-9_]` grammar, which
// means a period id is the date with `_` separators, not `-`.
// -----------------------------------------------------------------------------

export const tpoCalendarPeriodColTestId = (periodStartDate: string): string =>
    `tpo_calendar_col_period_${slugifyFieldName(periodStartDate)}`;
export const tpoCalendarPeriodCellTestId = (rowKey: string | number, periodStartDate: string): string =>
    `tpo_calendar_cell_${rowKey}_period_${slugifyFieldName(periodStartDate)}`;

/**
 * One event bar inside a period cell. `CalendarEvent` carries no stable key of its
 * own — only resolved display values — so the bar is addressed by its position
 * within the owning cell, which is itself keyed on the row and the period.
 */
export const tpoCalendarEventBarTestId = (cellTestId: string, index: number): string =>
    `${cellTestId}_event_${index}`;

/**
 * The row's `...` trigger. Row action menu entries derive from it via
 * `tpoOptionTestId`, so the whole menu is addressable from the row key alone.
 */
export const tpoRowActionsBtnTestId = (surface: string, rowKey: string | number): string =>
    tpoTableCellTestId(surface, rowKey, 'actions_btn');

/**
 * Keep indicator inside a calendar cell: the two promo-level flags on the promo
 * cell, the event-level one on the event cell. Keyed on the owning cell rather
 * than the row, because which column carries it depends on the tenant's promo
 * column key. Which flag is set is published as `data-state-keep-dates` /
 * `data-state-keep-promo` / `data-state-keep-event` on the same node.
 */
export const tpoKeepFlagIndicatorTestId = (cellTestId: string): string => `${cellTestId}_keep_indicator`;

// =============================================================================
// Static chrome — Customer config gate
//
// Every TPOGen screen renders behind this gate, so a suite needs to tell a
// config failure apart from a missing locator anywhere downstream.
// =============================================================================

export const TPO_CONFIG_GATE_LOADING = 'tpo_config_gate_loading';
export const TPO_CONFIG_GATE_ERROR = 'tpo_config_gate_error';

// =============================================================================
// Static chrome — Plan Index (`/tpo-gen`)
// =============================================================================

export const TPO_PLAN_INDEX_PAGE = 'tpo_plan_index_page';
export const TPO_PLAN_INDEX_LOADING = 'tpo_plan_index_loading';
export const TPO_PLAN_INDEX_EMPTY = 'tpo_plan_index_empty';
export const TPO_PLAN_INDEX_BREADCRUMB = 'tpo_plan_index_breadcrumb';
export const TPO_PLAN_INDEX_BREADCRUMB_ROOT_LINK = 'tpo_plan_index_breadcrumb_root_link';
export const TPO_PLAN_INDEX_TOOLBAR = 'tpo_plan_index_toolbar';
export const TPO_PLAN_INDEX_TAB_PLANS = 'tpo_plan_index_tab_plans';
export const TPO_PLAN_INDEX_TAB_COMPARISON = 'tpo_plan_index_tab_comparison';
export const TPO_PLAN_INDEX_MY_PLANS_SWITCH = 'tpo_plan_index_my_plans_switch';
export const TPO_PLAN_INDEX_SEARCH_INPUT = 'tpo_plan_index_search_input';
export const TPO_PLAN_INDEX_FILTER = 'tpo_plan_index_filter';
export const TPO_PLAN_INDEX_FILTER_RIBBON = 'tpo_plan_index_filter_ribbon';
export const TPO_PLAN_INDEX_FIELDS_BTN = 'tpo_plan_index_fields_btn';
export const TPO_PLAN_INDEX_FIELDS_POPOVER = 'tpo_plan_index_fields_popover';
export const TPO_PLAN_INDEX_CREATE_PLAN_BTN = 'tpo_plan_index_create_plan_btn';
export const TPO_PLAN_INDEX_TABLE = 'tpo_plan_index_table';
export const TPO_PLAN_INDEX_SCROLLER = 'tpo_plan_index_scroller';
export const TPO_PLAN_INDEX_ROW_LOADING = 'tpo_plan_index_row_loading';
export const TPO_PLAN_INDEX_CREATE_PLAN_ROW_BTN = 'tpo_plan_index_create_plan_row_btn';
export const TPO_PLAN_INDEX_CREATE_REPORT_BTN = 'tpo_plan_index_create_report_btn';
export const TPO_COMPARISON_REPORTS_TABLE = 'tpo_comparison_reports_table';
export const TPO_COMPARISON_REPORTS_SCROLLER = 'tpo_comparison_reports_scroller';
export const TPO_COMPARISON_REPORTS_LOADING = 'tpo_comparison_reports_loading';
export const TPO_COMPARISON_REPORTS_EMPTY = 'tpo_comparison_reports_empty';

export const TPO_CREATE_PLAN_MODAL = 'tpo_create_plan_modal';
export const TPO_CREATE_PLAN_FORM = 'tpo_create_plan_form';
export const TPO_CREATE_PLAN_SUBMIT_BTN = 'tpo_create_plan_submit_btn';
export const TPO_CREATE_PLAN_CANCEL_BTN = 'tpo_create_plan_cancel_btn';

// =============================================================================
// Static chrome — Plan Detail shell
// =============================================================================

export const TPO_PLAN_DETAILS_PAGE = 'tpo_plan_details_page';
export const TPO_PLAN_DETAILS_BREADCRUMB = 'tpo_plan_details_breadcrumb';
export const TPO_PLAN_DETAILS_BREADCRUMB_PLANS_LINK = 'tpo_plan_details_breadcrumb_plans_link';
export const TPO_PLAN_DETAILS_BREADCRUMB_GROUP_LINK = 'tpo_plan_details_breadcrumb_group_link';
export const TPO_PLAN_DETAILS_BREADCRUMB_LOADING = 'tpo_plan_details_breadcrumb_loading';
export const TPO_PLAN_DETAILS_TITLE = 'tpo_plan_details_title';
export const TPO_PLAN_DETAILS_SUMMARY = 'tpo_plan_details_summary';
export const TPO_PLAN_DETAILS_SUMMARY_CUSTOMER = 'tpo_plan_details_summary_customer';
export const TPO_PLAN_DETAILS_SUMMARY_CATEGORY = 'tpo_plan_details_summary_category';
export const TPO_PLAN_DETAILS_SUMMARY_YEAR = 'tpo_plan_details_summary_year';
export const TPO_PLAN_DETAILS_TABS = 'tpo_plan_details_tabs';
/** Planner tab shell; `data-state-active-view` says which view is mounted. */
export const TPO_PLANNER_TAB = 'tpo_planner_tab';
/** Optimization status icon; state carries `status` and `outcome`. */
export const TPO_OPTIMIZATION_STATUS_ICON = 'tpo_optimization_status_icon';
export const TPO_PLAN_DETAILS_TAB_PLAN = 'tpo_plan_details_tab_plan';
export const TPO_PLAN_DETAILS_TAB_CREATE_PLAN = 'tpo_plan_details_tab_create_plan';
export const TPO_PLAN_DETAILS_FOOTER = 'tpo_plan_details_footer';
export const TPO_PLAN_DETAILS_FOOTER_RECORD_COUNT = 'tpo_plan_details_footer_record_count';
export const TPO_PLAN_DETAILS_FOOTER_CREATED_BY = 'tpo_plan_details_footer_created_by';
export const TPO_PLAN_DETAILS_FOOTER_EDIT_HOLDER = 'tpo_plan_details_footer_edit_holder';
export const TPO_PLAN_VIEW_HEADER = 'tpo_plan_view_header';
export const TPO_PLAN_VIEW_PLAN_NAME = 'tpo_plan_view_plan_name';
export const TPO_PLAN_VIEW_TAB_CALENDAR = 'tpo_plan_view_tab_calendar';
export const TPO_PLAN_VIEW_TAB_METRIC = 'tpo_plan_view_tab_metric';
export const TPO_PLAN_VIEW_OPTIMIZED_SWITCH = 'tpo_plan_view_optimized_switch';
export const TPO_PLAN_VIEW_OPTIMIZATION_STATUS = 'tpo_plan_view_optimization_status';
export const TPO_PLAN_VIEW_OPERATIONAL_PLAN_ICON = 'tpo_plan_view_operational_plan_icon';

// -----------------------------------------------------------------------------
// Edit session (server-backed edit lock)
// -----------------------------------------------------------------------------
// The page root `tpo_plan_details_page` carries `data-state-mode="edit" | "view"`,
// which is the single locator QA should branch on. The session timer additionally
// exposes `data-state-expiring` when inside the warning window.
export const TPO_EDIT_SESSION_TIMER = 'tpo_edit_session_timer';
export const TPO_EDIT_SESSION_EXIT_BTN = 'tpo_edit_session_exit_btn';
// Only present on the Plan Actions "Edit plan" row while another user holds the
// lock, since that is the only state that wraps the label in a tooltip. It
// carries `data-state-held-by` so the holder can be asserted without hovering.
export const TPO_EDIT_PLAN_MENU_LABEL = 'tpo_edit_plan_menu_label';
export const TPO_EDIT_SESSION_LOST_MODAL = 'tpo_edit_session_lost_modal';
export const TPO_EDIT_SESSION_LOST_OK_BTN = 'tpo_edit_session_lost_ok_btn';
// Both confirm modals go through `ConfirmationModal`, which derives
// `${id}_confirm_btn` / `${id}_cancel_btn` for their two actions.
export const TPO_EDIT_PLAN_CONFIRM_MODAL = 'tpo_edit_plan_confirm_modal';
export const TPO_EDIT_PLAN_TAKEOVER_NOTE = 'tpo_edit_plan_takeover_note';
export const TPO_EXTEND_SESSION_MODAL = 'tpo_extend_session_modal';

/**
 * `ViewSelectModal` is reused for four different choices, so it has no id of its
 * own: each caller passes the id of the choice it is presenting
 * (`TPO_CALENDAR_INTERVAL_MODAL`, `TPO_METRIC_GRANULARITY_MODAL`,
 * `TPO_METRIC_KPI_VIEW_MODAL`, `TPO_METRIC_METRIC_TYPE_MODAL`) and the modal
 * derives `_apply_btn`, `_cancel_btn`, and one option id per choice from it
 * via `tpoOptionTestId`.
 */

// =============================================================================
// Static chrome — Calendar page
// =============================================================================

export const TPO_CALENDAR_PAGE = 'tpo_calendar_page';
export const TPO_CALENDAR_LOADING = 'tpo_calendar_loading';
export const TPO_CALENDAR_EMPTY = 'tpo_calendar_empty';
export const TPO_CALENDAR_TOOLBAR = 'tpo_calendar_toolbar';
export const TPO_CALENDAR_COLUMNS_SELECT = 'tpo_calendar_columns_select';
export const TPO_CALENDAR_SEARCH_INPUT = 'tpo_calendar_search_input';
export const TPO_CALENDAR_GROUP_BY_SELECT = 'tpo_calendar_group_by_select';
export const TPO_CALENDAR_ADD_EVENTS_BTN = 'tpo_calendar_add_events_btn';
export const TPO_CALENDAR_ADD_NEW_EVENT_ITEM = 'tpo_calendar_add_new_event_item';
export const TPO_CALENDAR_ADD_EVENTS_FROM_PLAN_ITEM = 'tpo_calendar_add_events_from_plan_item';
export const TPO_CALENDAR_FILTER = 'tpo_calendar_filter';
export const TPO_CALENDAR_FILTER_RIBBON = 'tpo_calendar_filter_ribbon';
export const TPO_CALENDAR_OPTIMIZE_PLAN_BTN = 'tpo_calendar_optimize_plan_btn';
export const TPO_CALENDAR_INFEASIBLE_FACTORS_BTN = 'tpo_calendar_infeasible_factors_btn';
export const TPO_CALENDAR_PLAN_ACTIONS_BTN = 'tpo_calendar_plan_actions_btn';
export const TPO_CALENDAR_INTERVAL_BTN = 'tpo_calendar_interval_btn';
export const TPO_CALENDAR_TABLE = 'tpo_calendar_table';
export const TPO_CALENDAR_SCROLLER = 'tpo_calendar_scroller';
export const TPO_CALENDAR_PROMO_TYPE_LEGEND = 'tpo_calendar_promo_type_legend';
export const TPO_CALENDAR_INTERVAL_MODAL = 'tpo_calendar_interval_modal';

// =============================================================================
// Static chrome — Events page
// =============================================================================

export const TPO_EVENTS_PAGE = 'tpo_events_page';
export const TPO_EVENTS_LOADING = 'tpo_events_loading';
export const TPO_EVENTS_TABS = 'tpo_events_tabs';
export const TPO_EVENTS_DETAILS_SECTION = 'tpo_events_details_section';
export const TPO_EVENTS_PROMO_DETAILS_SECTION = 'tpo_events_promo_details_section';
/** Switches the section between event details and product portfolio. */
export const TPO_EVENTS_VIEW_TOGGLE = 'tpo_events_view_toggle';
export const TPO_EVENTS_SAVE_BTN = 'tpo_events_save_btn';
export const TPO_EVENTS_CANCEL_BTN = 'tpo_events_cancel_btn';
export const TPO_EVENTS_ADD_PROMO_BTN = 'tpo_events_add_promo_btn';
export const TPO_EVENT_PROMO_SECTION = 'tpo_event_promo_section';
export const TPO_EVENT_PROMO_SECTION_EMPTY = 'tpo_event_promo_section_empty';

/**
 * Id for one tab strip entry on the Events page.
 *
 * `tabKey` is the runtime tab key (`1`, `promo-2`, `edit-promo-<promoId>`,
 * `add-promo`). It is slugified rather than passed through because the AntD keys
 * are hyphenated and a hyphen is outside the published grammar. The mapping is
 * still predictable from an API response: the only change is `-` to `_`, and an
 * embedded promo id survives it unchanged.
 */
export const tpoEventsTabTestId = (tabKey: string): string => `tpo_events_tab_${slugifyFieldName(tabKey)}`;

export const tpoEventPromoRowTestId = (promoId: string | number): string => `tpo_event_promo_row_${promoId}`;
export const tpoEventPromoActionsBtnTestId = (promoId: string | number): string =>
    `tpo_event_promo_actions_btn_${promoId}`;
export const tpoEventPromoEditItemTestId = (promoId: string | number): string => `tpo_event_promo_edit_item_${promoId}`;
export const tpoEventPromoDeleteItemTestId = (promoId: string | number): string =>
    `tpo_event_promo_delete_item_${promoId}`;

// =============================================================================
// Static chrome — Promo designer
// =============================================================================

export const TPO_PROMO_DESIGNER = 'tpo_promo_designer';
export const TPO_PROMO_FORM = 'tpo_promo_form';
export const TPO_PROMO_LOADING = 'tpo_promo_loading';
export const TPO_PROMO_TABS = 'tpo_promo_tabs';
export const TPO_PROMO_SAVE_BTN = 'tpo_promo_save_btn';
export const TPO_PROMO_CANCEL_BTN = 'tpo_promo_cancel_btn';
export const TPO_PROMO_METRICS_SECTION = 'tpo_promo_metrics_section';
export const TPO_PROMO_METRICS_TABLE = 'tpo_promo_metrics_table';
export const TPO_PROMO_METRICS_EMPTY = 'tpo_promo_metrics_empty';
export const TPO_PROMO_METRICS_LOADING = 'tpo_promo_metrics_loading';
export const TPO_PROMO_METRICS_LEGEND = 'tpo_promo_metrics_legend';
export const TPO_PROMO_METRICS_REFRESH_UPLIFT_BTN = 'tpo_promo_metrics_refresh_uplift_btn';
export const TPO_PROMO_METRICS_LOAD_MORE_BTN = 'tpo_promo_metrics_load_more_btn';
export const TPO_PROMO_METRICS_TOTALS_SWITCH = 'tpo_promo_metrics_totals_switch';
export const TPO_PROMO_METRICS_VIEW_TAB = 'tpo_promo_metrics_view_tab';
export const TPO_PROMO_SPENDS_VIEW_TAB = 'tpo_promo_spends_view_tab';
// KPI picker applies on click, so it has no Reset/Apply pair.
export const TPO_PROMO_KPI_SELECT_BTN = 'tpo_promo_kpi_select_btn';
export const TPO_PROMO_KPI_SELECT_PANEL = 'tpo_promo_kpi_select_panel';
export const TPO_PROMO_KPI_SEARCH_INPUT = 'tpo_promo_kpi_search_input';
export const TPO_PROMO_KPI_SELECT_ALL = 'tpo_promo_kpi_select_all';
export const TPO_PROMO_KPI_EMPTY = 'tpo_promo_kpi_empty';

export const TPO_PROMO_PRODUCT_SELECT_BTN = 'tpo_promo_product_select_btn';
export const TPO_PROMO_PRODUCT_SELECT_PANEL = 'tpo_promo_product_select_panel';
export const TPO_PROMO_PRODUCT_SEARCH_INPUT = 'tpo_promo_product_search_input';
export const TPO_PROMO_PRODUCT_SELECT_ALL = 'tpo_promo_product_select_all';
export const TPO_PROMO_PRODUCT_EMPTY = 'tpo_promo_product_empty';
export const TPO_PROMO_PRODUCT_LOADING = 'tpo_promo_product_loading';
export const TPO_PROMO_PRODUCT_RESET_BTN = 'tpo_promo_product_reset_btn';
export const TPO_PROMO_PRODUCT_APPLY_BTN = 'tpo_promo_product_apply_btn';

/** Promo tab, keyed on the tab key already in use. */
export const tpoPromoTabTestId = (tabKey: string): string => `tpo_promo_tab_${slugifyFieldName(tabKey)}`;

// =============================================================================
// Static chrome — Metric page
// =============================================================================

export const TPO_METRIC_PAGE = 'tpo_metric_page';
export const TPO_METRIC_TOOLBAR = 'tpo_metric_toolbar';
export const TPO_METRIC_DIMENSION_SELECT = 'tpo_metric_dimension_select';
export const TPO_METRIC_KPI_SELECT = 'tpo_metric_kpi_select';
export const TPO_METRIC_ATTRIBUTE_SELECT = 'tpo_metric_attribute_select';
export const TPO_METRIC_YEAR_SELECT = 'tpo_metric_year_select';
export const TPO_METRIC_VIEW_SELECT = 'tpo_metric_view_select';
export const TPO_METRIC_SEARCH_INPUT = 'tpo_metric_search_input';
export const TPO_METRIC_FILTER = 'tpo_metric_filter';
export const TPO_METRIC_FILTER_RIBBON = 'tpo_metric_filter_ribbon';
export const TPO_METRIC_PLAN_ACTIONS_BTN = 'tpo_metric_plan_actions_btn';
export const TPO_METRIC_SUB_MODE_SWITCH = 'tpo_metric_sub_mode_switch';
export const TPO_METRIC_TABLE = 'tpo_metric_table';
/** `MetricTable` derives both states from the table id (see `renderEmptyText`). */
export const TPO_METRIC_LOADING = `${TPO_METRIC_TABLE}_loading`;
export const TPO_METRIC_EMPTY = `${TPO_METRIC_TABLE}_empty`;
/** Trailing column header that opens the view/granularity menu. */
export const TPO_METRIC_MORE_BTN = 'tpo_metric_more_btn';
export const TPO_METRIC_GRANULARITY_ITEM = 'tpo_metric_granularity_item';
export const TPO_METRIC_KPI_VIEW_ITEM = 'tpo_metric_kpi_view_item';
export const TPO_METRIC_TYPE_ITEM = 'tpo_metric_type_item';
export const TPO_METRIC_BREADCRUMB = 'tpo_metric_breadcrumb';
export const TPO_METRIC_TREND_CHART_PANEL = 'tpo_metric_trend_chart_panel';
export const TPO_METRIC_GRANULARITY_MODAL = 'tpo_metric_granularity_modal';
export const TPO_METRIC_KPI_VIEW_MODAL = 'tpo_metric_kpi_view_modal';
export const TPO_METRIC_METRIC_TYPE_MODAL = 'tpo_metric_metric_type_modal';

// =============================================================================
// Static chrome — Optimize Plan
// =============================================================================

export const TPO_OPTIMIZE_MODAL = 'tpo_optimize_modal';
export const TPO_OPTIMIZE_TABS = 'tpo_optimize_tabs';
export const TPO_OPTIMIZE_TAB_SCOPE = 'tpo_optimize_tab_scope';
export const TPO_OPTIMIZE_TAB_CONSTRAINTS = 'tpo_optimize_tab_constraints';
export const TPO_OPTIMIZE_SCOPE_PANEL = 'tpo_optimize_scope_panel';
export const TPO_OPTIMIZE_CONSTRAINTS_PANEL = 'tpo_optimize_constraints_panel';
export const TPO_OPTIMIZE_CONSTRAINTS_TABLE = 'tpo_optimize_constraints_table';
export const TPO_OPTIMIZE_CONSTRAINT_SELECTOR = 'tpo_optimize_constraint_selector';
export const TPO_OPTIMIZE_RUN_BTN = 'tpo_optimize_run_btn';
export const TPO_OPTIMIZE_CANCEL_BTN = 'tpo_optimize_cancel_btn';
export const TPO_OPTIMIZE_SCOPE_SECTION = 'tpo_optimize_scope_section';
export const TPO_OPTIMIZE_BASE_INPUTS_SECTION = 'tpo_optimize_base_inputs_section';
export const TPO_OPTIMIZE_PROMOTION_COUNT = 'tpo_optimize_promotion_count';
export const TPO_OPTIMIZE_CONSTRAINTS_LOADING = 'tpo_optimize_constraints_loading';
export const TPO_OPTIMIZE_CONSTRAINTS_ERROR = 'tpo_optimize_constraints_error';
export const TPO_OPTIMIZE_STATUS = 'tpo_optimize_status';

/**
 * The Add Level dropdown. One instance per KPI group rather than the single
 * global level dropdown it replaces, so the per-KPI id derives from this base
 * via `tpoPartTestId` and the level options hang off that via `tpoOptionTestId`.
 * The base id is unchanged so existing level-option locators keep resolving.
 */
export const TPO_OPTIMIZE_LEVEL_SELECT = 'tpo_optimize_level_select';

/**
 * The Add Level trigger belonging to one KPI group. `constraintName` is an
 * `optimization.objectives[].constraints[].name` from the backend config, passed
 * through verbatim, so the set of ids follows the environment's registry.
 */
export const TPO_CONSTRAINT_ADD_LEVEL_TEST_ID = (constraintName: string): string =>
    tpoPartTestId(TPO_OPTIMIZE_LEVEL_SELECT, constraintName);

/** Delete affordance on a level block header or on a single attribute row. */
export const TPO_CONSTRAINT_REMOVE_TEST_ID = (rowTestId: string): string => `${rowTestId}_remove_btn`;

export const TPO_INFEASIBLE_PLAN_DRAWER = 'tpo_infeasible_plan_drawer';
export const TPO_INFEASIBLE_CONSTRAINT_GROUP = 'tpo_infeasible_constraint_group';
export const TPO_INFEASIBLE_CONSTRAINT_ROW = 'tpo_infeasible_constraint_row';

export const TPO_OPTIMISE_RESULT_MODAL = 'tpo_optimise_result_modal';
export const TPO_OPTIMISE_RESULT_SUMMARY = 'tpo_optimise_result_summary';
export const TPO_OPTIMISE_RESULT_TABLE = 'tpo_optimise_result_table';
export const TPO_OPTIMISE_RESULT_LOADING = 'tpo_optimise_result_loading';
export const TPO_OPTIMISE_RESULT_ERROR = 'tpo_optimise_result_error';

// =============================================================================
// Static chrome — Import events from plan
// =============================================================================

export const TPO_IMPORT_EVENTS_MODAL = 'tpo_import_events_modal';
export const TPO_IMPORT_EVENTS_PLAN_SELECT = 'tpo_import_events_plan_select';
export const TPO_IMPORT_EVENTS_SEARCH_INPUT = 'tpo_import_events_search_input';
export const TPO_IMPORT_EVENTS_TABLE = 'tpo_import_events_table';
export const TPO_IMPORT_EVENTS_EMPTY = 'tpo_import_events_empty';
export const TPO_IMPORT_EVENTS_LOADING = 'tpo_import_events_loading';
export const TPO_IMPORT_EVENTS_CONFIRM_BTN = 'tpo_import_events_confirm_btn';
export const TPO_IMPORT_EVENTS_CANCEL_BTN = 'tpo_import_events_cancel_btn';
/**
 * Chooses whether the source plan list is drawn from TPM or TPO plans. Carries
 * `data-state-plan-type="tpm" | "tpo"` so a suite can assert which list the
 * plan select is showing without reading the switch's internal markup.
 */
export const TPO_IMPORT_EVENTS_PLAN_TYPE_SWITCH = 'tpo_import_events_plan_type_switch';
/**
 * Counter below the events table. The table pages in on scroll, so the counts
 * are published as `data-state-loaded-count` / `data-state-total-count` /
 * `data-state-selected-count` rather than left for QA to parse out of the
 * rendered sentence, which switches wording once a row is selected.
 */
export const TPO_IMPORT_EVENTS_FOOTER = 'tpo_import_events_footer';
export const TPO_IMPORT_EVENTS_RECORD_COUNT = 'tpo_import_events_record_count';

// =============================================================================
// Static chrome — Report export
// =============================================================================
// One modal serves all four report kinds (calendar, plan metric, promo metric,
// plan comparison), so the ids do not name the kind. Which report is being
// exported is established by the menu entry that opened the modal, and that
// entry already carries its own config-derived id.

export const TPO_REPORT_EXPORT_MODAL = 'tpo_report_export_modal';
export const TPO_REPORT_EXPORT_CONFIRM_BTN = `${TPO_REPORT_EXPORT_MODAL}_confirm_btn`;
export const TPO_REPORT_EXPORT_CANCEL_BTN = `${TPO_REPORT_EXPORT_MODAL}_cancel_btn`;
/**
 * Scope radios. The two options derive from the `ExportScope` values through
 * `tpoOptionTestId`, giving `..._option_current_view` and `..._option_all_data`
 * — keyed on the value, never on the visible label.
 */
export const TPO_REPORT_EXPORT_SCOPE_GROUP = 'tpo_report_export_scope_group';

// =============================================================================
// Static chrome — Confirmation modals
// =============================================================================
// These six modals all render through the shared `ConfirmationModal`, which
// derives its footer buttons as `${root}_confirm_btn` / `${root}_cancel_btn`.
// The button constants below therefore restate that derivation rather than
// inventing a second naming scheme — keep them in sync with the shared
// component if that derivation ever changes.
//
// No component imports them, by design: the shared modal builds the string
// itself from the root id. They are declared so the ids reach the published
// manifest, so an unused-export report is not a reason to delete them.

export const TPO_DELETE_PLAN_MODAL = 'tpo_delete_plan_modal';
export const TPO_DELETE_PLAN_CONFIRM_BTN = `${TPO_DELETE_PLAN_MODAL}_confirm_btn`;
export const TPO_DELETE_PLAN_CANCEL_BTN = `${TPO_DELETE_PLAN_MODAL}_cancel_btn`;

export const TPO_DELETE_EVENT_MODAL = 'tpo_delete_event_modal';
export const TPO_DELETE_EVENT_CONFIRM_BTN = `${TPO_DELETE_EVENT_MODAL}_confirm_btn`;
export const TPO_DELETE_EVENT_CANCEL_BTN = `${TPO_DELETE_EVENT_MODAL}_cancel_btn`;

export const TPO_DELETE_ALL_PROMOTIONS_MODAL = 'tpo_delete_all_promotions_modal';
export const TPO_DELETE_ALL_PROMOTIONS_CONFIRM_BTN = `${TPO_DELETE_ALL_PROMOTIONS_MODAL}_confirm_btn`;
export const TPO_DELETE_ALL_PROMOTIONS_CANCEL_BTN = `${TPO_DELETE_ALL_PROMOTIONS_MODAL}_cancel_btn`;

export const TPO_DELETE_PROMO_MODAL = 'tpo_delete_promo_modal';
export const TPO_DELETE_PROMO_CONFIRM_BTN = `${TPO_DELETE_PROMO_MODAL}_confirm_btn`;
export const TPO_DELETE_PROMO_CANCEL_BTN = `${TPO_DELETE_PROMO_MODAL}_cancel_btn`;

export const TPO_UNSAVED_CHANGES_MODAL = 'tpo_unsaved_changes_modal';
export const TPO_UNSAVED_CHANGES_CONFIRM_BTN = `${TPO_UNSAVED_CHANGES_MODAL}_confirm_btn`;
export const TPO_UNSAVED_CHANGES_CANCEL_BTN = `${TPO_UNSAVED_CHANGES_MODAL}_cancel_btn`;

export const TPO_NO_PROMOS_MODAL = 'tpo_no_promos_modal';
export const TPO_NO_PROMOS_CONFIRM_BTN = `${TPO_NO_PROMOS_MODAL}_confirm_btn`;
export const TPO_NO_PROMOS_CANCEL_BTN = `${TPO_NO_PROMOS_MODAL}_cancel_btn`;

export const TPO_SAVE_AS_PLAN_MODAL = 'tpo_save_as_plan_modal';
export const TPO_SAVE_AS_PLAN_CONFIRM_BTN = `${TPO_SAVE_AS_PLAN_MODAL}_confirm_btn`;
export const TPO_SAVE_AS_PLAN_CANCEL_BTN = `${TPO_SAVE_AS_PLAN_MODAL}_cancel_btn`;
// The Save As modal is the one confirmation dialog with a body input; the modal
// forwards this id onto the name field explicitly.
export const TPO_SAVE_AS_PLAN_NAME_INPUT = 'tpo_save_as_plan_name_input';

export const TPO_SAVE_AS_ORIGINAL_MODAL = 'tpo_save_as_original_modal';
export const TPO_SAVE_AS_ORIGINAL_CONFIRM_BTN = `${TPO_SAVE_AS_ORIGINAL_MODAL}_confirm_btn`;
export const TPO_SAVE_AS_ORIGINAL_CANCEL_BTN = `${TPO_SAVE_AS_ORIGINAL_MODAL}_cancel_btn`;

// One modal serves both keep flags and both directions. Which one is in play is
// not republished here: the modal is only reachable from a specific row action,
// and that menu entry already carries its own id.
export const TPO_KEEP_FLAGS_MODAL = 'tpo_keep_flags_modal';
export const TPO_KEEP_FLAGS_CONFIRM_BTN = `${TPO_KEEP_FLAGS_MODAL}_confirm_btn`;
export const TPO_KEEP_FLAGS_CANCEL_BTN = `${TPO_KEEP_FLAGS_MODAL}_cancel_btn`;

// The event-level keep action is a separate modal because it writes a different
// endpoint and cascades onto every promo of the event. Like the keep-flags one
// it serves both directions, and the row action it opens from carries its own id.
export const TPO_KEEP_EVENT_MODAL = 'tpo_keep_event_modal';
export const TPO_KEEP_EVENT_CONFIRM_BTN = `${TPO_KEEP_EVENT_MODAL}_confirm_btn`;
export const TPO_KEEP_EVENT_CANCEL_BTN = `${TPO_KEEP_EVENT_MODAL}_cancel_btn`;

// =============================================================================
// Static chrome — Scenario Comparison (`/tpo-gen/scenario-comparison/:reportId`)
// =============================================================================

export const TPO_SCENARIO_COMPARISON_PAGE = 'tpo_scenario_comparison_page';
export const TPO_SCENARIO_COMPARISON_LOADING = 'tpo_scenario_comparison_loading';
export const TPO_SCENARIO_COMPARISON_BREADCRUMB = 'tpo_scenario_comparison_breadcrumb';
/** Shown when the saved base plan no longer exists; holds the recovery action. */
export const TPO_SCENARIO_COMPARISON_BASE_MISSING_ALERT = 'tpo_scenario_comparison_base_missing_alert';
export const TPO_SCENARIO_COMPARISON_BASE_MISSING_BTN = 'tpo_scenario_comparison_base_missing_btn';
/** Shown when some saved comparison plans could not be resolved. */
export const TPO_SCENARIO_COMPARISON_SCENARIOS_MISSING_ALERT = 'tpo_scenario_comparison_scenarios_missing_alert';
/** Names the report on screen, so it doubles as the "right report is open" assertion. */
export const TPO_SCENARIO_COMPARISON_REPORT_NAME = 'tpo_scenario_comparison_report_name';
export const TPO_SCENARIO_COMPARISON_REPORT_NAME_INPUT = 'tpo_scenario_comparison_report_name_input';
export const TPO_SCENARIO_COMPARISON_EDIT_NAME_BTN = 'tpo_scenario_comparison_edit_name_btn';

export const TPO_SCENARIO_COMPARISON_TOOLBAR = 'tpo_scenario_comparison_toolbar';
/** Multi-select over the KPI catalog; options and bulk toggle derive from it. */
export const TPO_SCENARIO_COMPARISON_KPI_SELECT = 'tpo_scenario_comparison_kpi_select';
export const TPO_SCENARIO_COMPARISON_SEARCH_INPUT = 'tpo_scenario_comparison_search_input';
/** Absolute/percent toggle; `data-state-checked` is set while percent is on. */
export const TPO_SCENARIO_COMPARISON_PERCENT_SWITCH = 'tpo_scenario_comparison_percent_switch';
export const TPO_SCENARIO_COMPARISON_CHANGE_BASE_BTN = 'tpo_scenario_comparison_change_base_btn';
export const TPO_SCENARIO_COMPARISON_ADD_SCENARIO_BTN = 'tpo_scenario_comparison_add_scenario_btn';
export const TPO_SCENARIO_COMPARISON_SAVE_BTN = 'tpo_scenario_comparison_save_btn';
/** Overflow menu; its rows derive from the config `scenarioComparisonMenuItems` keys. */
export const TPO_SCENARIO_COMPARISON_ACTIONS_BTN = 'tpo_scenario_comparison_actions_btn';

export const TPO_SCENARIO_COMPARISON_GRID = 'tpo_scenario_comparison_grid';
/** One placeholder per unused comparison slot, so several can be in the DOM at once. */
export const TPO_SCENARIO_COMPARISON_ADD_SLOT = 'tpo_scenario_comparison_add_slot';

/**
 * The base column answers to a fixed key rather than to the plan currently in
 * it, because "the base column" is what an assertion is aiming at and the plan
 * behind it changes whenever the base is swapped.
 */
export const SCENARIO_BASE_COLUMN_KEY = 'base';

/**
 * Grid column, keyed on the comparison plan id or on `SCENARIO_BASE_COLUMN_KEY`.
 * Plan ids are opaque backend identities and pass through verbatim, matching the
 * generic table helpers above.
 */
export const tpoScenarioColTestId = (columnKey: string): string => `tpo_scenario_comparison_col_${columnKey}`;

/**
 * One KPI row in the grid's leading label column.
 *
 * KPI keys are slugified rather than passed through for the same reason the
 * promo metric helpers slugify theirs: the live catalog serves keys such as
 * `event_Inc_gross_margin_dollars`, whose capitals would fall outside the
 * published grammar.
 */
export const tpoScenarioKpiRowTestId = (kpiKey: string): string =>
    `tpo_scenario_comparison_row_${slugifyFieldName(kpiKey)}`;

/** One value cell of the grid: a KPI row within a scenario column. */
export const tpoScenarioCellTestId = (columnKey: string, kpiKey: string): string =>
    `tpo_scenario_comparison_cell_${columnKey}_${slugifyFieldName(kpiKey)}`;

// -----------------------------------------------------------------------------
// KPI breakdown table
//
// Columns are addressed through the generic `tpoTableColTestId` helpers on the
// `scenario_breakdown` surface: the leading column by the config breakdown level
// key, the value columns by plan id or `SCENARIO_BASE_COLUMN_KEY`.
// -----------------------------------------------------------------------------

export const TPO_SCENARIO_BREAKDOWN_SECTION = 'tpo_scenario_breakdown_section';
export const TPO_SCENARIO_BREAKDOWN_TITLE = 'tpo_scenario_breakdown_title';
export const TPO_SCENARIO_BREAKDOWN_LEVEL_SELECT = 'tpo_scenario_breakdown_level_select';
export const TPO_SCENARIO_BREAKDOWN_SEARCH_INPUT = 'tpo_scenario_breakdown_search_input';
export const TPO_SCENARIO_BREAKDOWN_TABLE = 'tpo_scenario_breakdown_table';
export const TPO_SCENARIO_BREAKDOWN_LOADING = 'tpo_scenario_breakdown_loading';
export const TPO_SCENARIO_BREAKDOWN_EMPTY = 'tpo_scenario_breakdown_empty';

/**
 * One breakdown row, keyed on the grouping value the backend returned for the
 * active level (a brand or category name, not an id). Slugified for the same
 * reason as the KPI keys, and `undefined` when the value slugs to nothing — a
 * row with no grouping value has no identity worth publishing, and every such
 * row would otherwise answer to the same locator.
 */
export const tpoScenarioBreakdownRowTestId = (groupValue: string): string | undefined => {
    const slug = slugifyFieldName(groupValue);
    return slug ? `tpo_scenario_breakdown_row_${slug}` : undefined;
};

// -----------------------------------------------------------------------------
// Scenario Comparison modals
//
// `PlanSelectionModal` is rendered twice on the page — once to change the base
// and once to add scenarios — so it has no id of its own: each caller passes the
// id of the choice it is presenting and the modal derives its search box, plan
// options, empty state, and footer buttons from it.
// -----------------------------------------------------------------------------

export const TPO_CREATE_REPORT_MODAL = 'tpo_create_report_modal';
export const TPO_CHANGE_BASE_MODAL = 'tpo_change_base_modal';
export const TPO_ADD_SCENARIO_MODAL = 'tpo_add_scenario_modal';

export const TPO_DELETE_REPORT_MODAL = 'tpo_delete_report_modal';
export const TPO_DELETE_REPORT_CONFIRM_BTN = `${TPO_DELETE_REPORT_MODAL}_confirm_btn`;
export const TPO_DELETE_REPORT_CANCEL_BTN = `${TPO_DELETE_REPORT_MODAL}_cancel_btn`;
